import { darken, lighten } from '@material-ui/core'
import { deepPurple, lightBlue, pink } from '@material-ui/core/colors'

import { merge } from 'lodash-es'

import { Percept } from '../components/Logos'

import { AppThemeOptions } from './themeAugmentation'


type PerceptPaletteKey = (
  | 'cream'
  | 'blue'
  | 'turquoise'
  | 'red'
  | 'grey'
  | 'black'
  | 'yellow'
  | 'pink'
  | 'purple'
  | 'orange'
  | 'green'
)

type ThemePaletteKey = (
  | 'primary'
  | 'secondary'
  | 'neutral'
  | 'background'
  | 'paper'
  | 'text'
  | 'info'
  | 'success'
  | 'warning'
  | 'error'
  | 'contrastPanel'
  | 'infoRangeStart'
  | 'infoRangeStop'
)

export type ThemePalette = Record<ThemePaletteKey, string>


type PerceptPalette = Record<PerceptPaletteKey, string>

const originalPerceptPalette: PerceptPalette = {
  blue: '#16D2E8',
  turquoise: '#00fec0',
  red: '#EA1600',
  grey: '#434343',
  cream: '#FFFEF9',
  black: '#000',
  yellow: '#f9f36b',
  pink: '#E53F71',
  purple: '#5300D0',
  orange: '#ff6523',
  green: '#29F4B3',
}

const perceptPaletteLight: PerceptPalette = {
  ...originalPerceptPalette,
  yellow: '#F0DB5B',
  green: 'rgb(29, 183, 84)',
  red: 'rgb(236, 20, 20)',
}

const perceptPaletteDark: PerceptPalette = {
  ...originalPerceptPalette,
  red: 'rgb(255, 93, 76)',
  purple: deepPurple[400],
  pink: pink[400],
}

// Header font - Rubik
// Body font - Rubik


const getExpandedGenericChartColours = (colours: string[]): string[] => [
  ...colours,
  ...colours.map( colour => darken(colour, 0.3) ),
  ...colours.map( colour => lighten(colour, 0.35) ),
]


export type AppThemeColourisationProps = {
  themePalette: ThemePalette
  genericChartColours: string[]
}

export type AppThemeFontWeights = Record<
  | 'light'
  | 'regular'
  | 'medium'
  | 'bold'
  | 'header'
  | 'button'
  | 'subtitle'
  | 'caption'
  | 'chip'
  | 'input',
  number
>

const defaultFontWeights: AppThemeFontWeights = {
  light: 300,
  regular: 400,
  medium: 400,
  bold: 500,
  header: 500,
  button: 500,
  subtitle: 500,
  caption: 500,
  chip: 500,
  input: 500,
}

export type ThemeOptionsProps = (
  AppThemeColourisationProps
  & {
    fontWeights?: Partial<AppThemeFontWeights>
  }
)

export const getBaseLightPerceptThemeOptions = ({
  themePalette,
  genericChartColours,
  fontWeights = {},
}: ThemeOptionsProps): AppThemeOptions => {
  const resolvedFontWeights: AppThemeFontWeights = {...defaultFontWeights, ...fontWeights}
  return {
    branding: {
      label: 'Percept',
      LogoComponent: Percept,
    },
    palette: {
      type: 'light',
      neutral: {
        main: themePalette.neutral,
      },
      primary: {
        main: themePalette.primary,
      },
      secondary: {
        main: themePalette.secondary,
      },
      action: {
        active: themePalette.text,
        disabled: 'rgb(150, 150, 150)',
        disabledBackground: 'rgb(240, 240, 240)',
      },
      background: {
        default: themePalette.background,
        paper: themePalette.paper,
      },
      text: {
        primary: themePalette.text,
        secondary: 'rgba(0, 0, 0, 0.65)',
        hint: '#CCC',
      },
      error: {
        light: lighten(themePalette.error, 0.1),
        main: themePalette.error,
        dark: darken(themePalette.error, 0.1),
      },
      warning: {
        light: lighten(themePalette.warning, 0.1),
        main: themePalette.warning,
        dark: darken(themePalette.warning, 0.1),
      },
      success: {
        light: lighten(themePalette.success, 0.1),
        main: themePalette.success,
        dark: darken(themePalette.success, 0.1),
      },
      info: {
        light: lighten(themePalette.info, 0.1),
        main: themePalette.info,
        dark: darken(themePalette.info, 0.1),
        contrastText: perceptPaletteDark.grey,
      },
      channel: {
        search: {
          light: lightBlue['500'],
          main: lightBlue['700'],
          dark: lightBlue['800'],
        },
        social: {
          light: lighten(perceptPaletteLight.purple, 0.1),
          main: perceptPaletteLight.purple,
          dark: darken(perceptPaletteLight.purple, 0.1),
        },
        programmatic: {
          light: lighten(perceptPaletteLight.orange, 0.1),
          main: perceptPaletteLight.orange,
          dark: darken(perceptPaletteLight.orange, 0.1),
          contrastText: '#FFF',
        },
      },
    },
    shape: {
      borderRadius: 4,
    },
    chart: {
      healthColourStops: [
        themePalette.error,
        themePalette.warning,
        themePalette.success,
      ],
      informationalColourStops: [
        themePalette.infoRangeStart,
        themePalette.infoRangeStop,
      ],
      genericColourStops: getExpandedGenericChartColours(genericChartColours),
      grid: {
        stroke: 'rgb(220, 220, 220)',
      },
      axis: {
        stroke: 'rgb(200, 200, 200)',
      },
      tick: {
        stroke: 'rgb(180, 180, 180)',
      },
      tooltip: {
        valueColor: themePalette.text,
        valueBackgroundColor: 'rgb(240, 240, 240)',
        secondaryContentColor: 'rgba(0, 0, 0, 0.6)',
      },
    },
    typography: {
      fontFamily: 'Rubik, Helvetica Neue, Arial, Helvetica, sans-serif',
      fontWeightLight: resolvedFontWeights.light,
      fontWeightRegular: resolvedFontWeights.regular,
      fontWeightMedium: resolvedFontWeights.medium,
      fontWeightBold: resolvedFontWeights.bold,
      fontSize: 14,
      button: {
        fontWeight: resolvedFontWeights.button,
      },
      subtitle1: {
        fontSize: 12,
        lineHeight: '16px',
        fontWeight: resolvedFontWeights.subtitle,
      },
      body1: {
        fontSize: 14,
        lineHeight: '22px',
      },
      body2: {
        fontSize: 12,
        lineHeight: '20px',
      },
      caption: {
        fontWeight: resolvedFontWeights.caption,
        fontSize: 10,
        lineHeight: '16px',
      },
      h1: {
        fontWeight: resolvedFontWeights.header,
        fontSize: 40,
        lineHeight: '48px',
      },
      h2: {
        fontWeight: resolvedFontWeights.header,
        fontSize: 32,
        lineHeight: '40px',
      },
      h3: {
        fontWeight: resolvedFontWeights.header,
        fontSize: 26,
        lineHeight: '34px',
      },
      h4: {
        fontWeight: resolvedFontWeights.header,
        fontSize: 24,
        lineHeight: '28px',
      },
      h5: {
        fontWeight: resolvedFontWeights.header,
        fontSize: 16,
        lineHeight: '24px',
      },
      h6: {
        fontWeight: resolvedFontWeights.header,
        fontSize: 16,
        lineHeight: '24px',
      },
    },
    appComponents: {
      appBarTabIndicator: {
        colorPrimary: {
          backgroundColor: '#FFF',
        },
        colorSecondary: {
          backgroundColor: '#FFF',
        }
      },
      authenticator: {
        root: {
          // backgroundColor: 'rgba(255, 255, 255, 0.75)',
        },
        heading: {
          fontWeight: resolvedFontWeights.header,
          fontSize: 24,
        },
        input: {
          color: themePalette.text,
          backgroundColor: 'transparent',
          borderRadius: 6,
          fontWeight: resolvedFontWeights.input,
        }
      },
      contrastPanel: {
        root: {
          color: '#FFF',
          backgroundColor: themePalette.contrastPanel,
          borderRadius: 6,
        },
      },
      performanceTable: {
        cellCompact: {
          fontWeight: 500,
          fontSize: 14,
          padding: '12px 8px',
          '@media (min-width: 1500px)': {
            fontSize: 14,
          },
          '@media (max-width: 600px)': {
            fontSize: 13,
          },
          '@media (min-width: 1920px)': {
            fontSize: 15,
          },
        },
      },
      reportDashboard: {
        secondaryHealth: {
          backgroundColor: '#FFF',
        },
      },
      table: {
        footer: {
          color: '#FFF',
          backgroundColor: themePalette.contrastPanel,
          borderColor: 'rgba(255,255,255,0.085)',
        },
        groupedRowColors: ['#eee', '#ddd'],
      },
      insights: {
        recommendation: {
          color: themePalette.primary,
        },
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          '@global': {
            '@font-face': 'Outfit',
          },
        },
      },
    },
    props: {
      MuiButton: {
        disableElevation: true,
      },
      MuiPaper: {
        elevation: 0,
      },
      MuiCard: {
        elevation: 0,
      },
    },
    overrides: {
      MuiButtonBase: {
        root: {
          fontWeight: resolvedFontWeights.button,
        },
      },
      MuiCard: {
        root: {
          background: themePalette.paper,
        },
      },
      MuiChip: {
        root: {
          fontWeight: resolvedFontWeights.chip,
        },
        outlined: {
          color: themePalette.text,
        },
      },
      MuiDivider: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.12)',
        },
      },
      MuiIconButton: {
        root: {
          padding: '4px',
        },
      },
      MuiInputBase: {
        root: {
          fontWeight: resolvedFontWeights.input,
        },
        input: {
          padding: '6px 4px 7px',
        },
      },
      /* @material-ui/pickers */
      
      MuiPickersToolbar: {
        toolbar: {
          color: '#FFF',
          backgroundColor: themePalette.warning,
        },
      },
      MuiPickersCalendarHeader: {
        dayLabel: {
          color: 'rgba(0,0,0,0.85)',
          fontWeight: resolvedFontWeights.header,
        },
        switchHeader: {
          color: themePalette.text,
        },
        iconButton: {
          color: 'rgba(0,0,0,0.85)',
        },
      },
      MuiPickerDTTabs: {
        tabs: {
          backgroundColor: themePalette.primary,
        },
      },
      MuiPickersClock: {
        pin: {
          backgroundColor: themePalette.primary,
        },
      },
      MuiPickersClockPointer:{
        pointer: {
          backgroundColor: themePalette.primary,
        },
        noPoint: {
          backgroundColor: themePalette.primary,
        },
        thumb: {
          backgroundColor: themePalette.primary,
          border: `14px solid ${themePalette.primary}`,
        },
      },
      MuiPickersDay: {
        daySelected: {
          backgroundColor: themePalette.primary,
          '&:hover': {
            backgroundColor: themePalette.primary,
          },
          '@media (hover: none)': {
            '&:hover': {
              backgroundColor: `${themePalette.primary} !important`,
            },
          },
        },
        dayDisabled: {
          color: 'rgba(0,0,0,0.35)',
        },
      },
      MuiPickersYear: {
        yearDisabled: {
          color: 'rgba(0,0,0,0.35)',
        },
      },
      MuiTablePagination: {
        toolbar: {
          minHeight: 40,
        },
      },
      MuiTableCell: {
        root: {
          color: 'inherit',
          fontSize: 13,
        },
        body: {
          color: 'inherit',
        },
        footer: {
          color: 'inherit',
          fontSize: 13,
          lineHeight: 'unset',
        },
      },
    },
  }
}


const perceptThemePaletteLight: ThemePalette = {
  primary: '#5300D0',
  secondary: '#E53F71',
  neutral: '#434343',
  background: '#f8f6ed',
  paper: '#FFFEF9',
  text: '#000',
  info: '#16D2E8',
  success: 'rgb(29, 183, 84)',
  warning: '#F0DB5B',
  error: 'rgb(236, 20, 20)',
  contrastPanel: '#434343',
  infoRangeStart: '#16D2E8',
  infoRangeStop: '#5300D0',
}

const perceptGenericChartColoursLight: string[] = [
  'rgb(39, 128, 149)', // or 'rgb(44, 144, 167)'
  '#16D2E8',
  '#F0DB5B',
  'rgb(17, 103, 220)',
  'rgb(123, 206, 186)',
  // '#00fec0',
  '#5300D0',
  '#ff6523',
  '#E53F71',
]


export const baseLightPerceptThemeOptions = getBaseLightPerceptThemeOptions({
  themePalette: perceptThemePaletteLight,
  genericChartColours: perceptGenericChartColoursLight,
})


const perceptThemePaletteDark: ThemePalette = {
  primary: '#00fec0',
  secondary: '#E53F71',
  neutral: lighten('#434343', 0.2),
  background: '#434343',
  paper: darken('#434343', 0.15),
  info: '#16D2E8',
  text: '#FFFEF9',
  success: '#29F4B3',
  warning: '#f9f36b',
  error: 'rgb(255, 93, 76)',
  contrastPanel: '#434343',
  infoRangeStart: '#16D2E8',
  infoRangeStop: deepPurple[400],
}

const perceptGenericChartColoursDark: string[] = [
  // 'rgb(255, 93, 76)',
  // '#16D2E8',
  // '#E53F71',
  // '#f9f36b',
  // deepPurple[400],
  // '#00fec0',
  // '#29F4B3',

  // 'rgb(39, 128, 149)', // or 'rgb(44, 144, 167)'
  'rgb(45, 157, 183)',
  // '#16D2E8',
  'rgb(49, 229, 250)',
  '#F0DB5B',
  // 'rgb(17, 103, 220)',
  'rgb(43, 144, 234)',
  // 'rgb(123, 206, 186)',
  'rgb(143, 237, 215)',
  // '#00fec0',
  '#5300D0',
  '#ff6523',
  '#E53F71',
]


export const getBaseDarkPerceptThemeOptions = ({
  themePalette,
  genericChartColours,
  ...props
}: ThemeOptionsProps): AppThemeOptions => {
  return merge(
    {},
    getBaseLightPerceptThemeOptions({
      themePalette,
      genericChartColours,
      ...props
    }),
    {
      palette: {
        type: 'dark',
        primary: {
          contrastText: '#434343',
        },
        action: {
          active: '#FFF',
          disabled: 'rgb(200, 200, 200)',
          disabledBackground: 'rgb(75, 75, 75)',
        },
        text: {
          secondary: 'rgba(255, 255, 255, 0.65)',
          hint: darken('#FFFEF9', 0.2),
        },
        channel: {
          search: {
            light: lightBlue['500'],
            main: lightBlue['600'],
            dark: lightBlue['800'],
          },
          social: {
            light: lighten(perceptPaletteDark.purple, 0.25),
            main: lighten(perceptPaletteDark.purple, 0.15),
            dark: lighten(perceptPaletteDark.purple, 0.05),
          },
          programmatic: {
            light: lighten(perceptPaletteDark.orange, 0.1),
            main: perceptPaletteDark.orange,
            dark: darken(perceptPaletteDark.orange, 0.1),
            contrastText: '#FFF',
          },
        },
      },
      chart: {
        grid: {
          stroke: 'rgb(80, 80, 80)',
        },
        axis: {
          stroke: 'rgb(100, 100, 100)',
        },
        tick: {
          stroke: 'rgb(90, 90, 90)',
        },
        tooltip: {
          valueColor: '#FFF',
          valueBackgroundColor: 'rgb(40, 40, 40)',
          secondaryContentColor: 'rgba(255, 255, 255, 0.6)',
        },
      },
      appComponents: {
        authenticator: {
          input: {
            color: '#FFF',
          },
        },
        table: {
          footer: {
            color: '#FFF',
            backgroundColor: themePalette.paper,
            borderColor: 'rgba(255,255,255,0.085)',
          },
          groupedRowColors: ['#555', '#666'],
        },
        insights: {
          recommendation: {
            color: themePalette.warning,
          },
        },
      },
      overrides: {
        MuiDivider: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.25)',
          },
        },
  
        /* material-ui-pickers */
        MuiPickersCalendarHeader: {
          dayLabel: {
            color: 'rgba(255,255,255,0.85)',
            fontWeight: 500,
          },
          switchHeader: {
            color: '#FFF',
          },
          iconButton: {
            color: 'rgba(255,255,255,0.85)',
          },
        },
        MuiPickersDay: {
          dayDisabled: {
            color: 'rgba(255,255,255,0.35)',
          },
        },
        MuiPickersYear: {
          yearDisabled: {
            color: 'rgba(255,255,255,0.35)',
          },
        },
      },
    }
  )
}


export const baseDarkPerceptThemeOptions: AppThemeOptions = getBaseDarkPerceptThemeOptions({
  themePalette: perceptThemePaletteDark,
  genericChartColours: perceptGenericChartColoursDark,
})
